import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { allArticles } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  shoes: 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20designer%20sneakers%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-shoes&orientation=squarish',
  hoodies: 'https://readdy.ai/api/search-image?query=Soft%20grey%20hoodie%20isolated%20on%20white%2C%20clean%20product%20photo%2C%20minimalist%20fashion%20ecommerce%20style&width=120&height=120&seq=nav-img-hoodies&orientation=squarish',
  tshirts: 'https://readdy.ai/api/search-image?query=Plain%20white%20t-shirt%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-tshirts&orientation=squarish',
  jackets: 'https://readdy.ai/api/search-image?query=Black%20leather%20jacket%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-jackets&orientation=squarish',
  pants: 'https://readdy.ai/api/search-image?query=Khaki%20chino%20pants%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-pants&orientation=squarish',
  headwear: 'https://readdy.ai/api/search-image?query=Black%20baseball%20cap%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-headwear&orientation=squarish',
  sets: 'https://readdy.ai/api/search-image?query=Matching%20outfit%20set%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-sets&orientation=squarish',
  underwear: 'https://readdy.ai/api/search-image?query=Cotton%20underwear%20set%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-underwear&orientation=squarish',
  jersey: 'https://readdy.ai/api/search-image?query=Sports%20jersey%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-jersey&orientation=squarish',
  accessories: 'https://readdy.ai/api/search-image?query=Silver%20watch%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-accessories&orientation=squarish',
  bags: 'https://readdy.ai/api/search-image?query=Brown%20leather%20bag%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-bags&orientation=squarish',
  belts: 'https://readdy.ai/api/search-image?query=Leather%20belt%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-belts&orientation=squarish',
  perfumes: 'https://readdy.ai/api/search-image?query=Perfume%20bottle%20on%20white%20background%2C%20clean%20product%20photography%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-perfumes&orientation=squarish',
  other: 'https://readdy.ai/api/search-image?query=Assorted%20lifestyle%20products%20on%20white%20background%2C%20clean%20product%20photo%2C%20minimalist%20ecommerce%20style&width=120&height=120&seq=nav-img-other&orientation=squarish',
};

export default function Navbar() {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isMobileArticlesOpen, setIsMobileArticlesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-heading text-xl font-bold text-foreground-950 tracking-tight">yoybuyspreadsheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5 align-text-bottom">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              {t('footer.home')}
            </Link>

            <Link
              to="/article/rukovodstvo-yoybuy-spreadsheet"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5 align-text-bottom">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Гид по покупкам
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white rounded-lg border border-background-200/70 shadow-lg py-3 w-[480px] max-h-[420px] overflow-y-auto z-50">
                  <div className="grid grid-cols-2 gap-1 px-3">
                    {categories.map((cat) => (
                      <a
                        key={cat.key}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-50 transition-colors cursor-pointer group"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                          <img
                            src={categoryImages[cat.key]}
                            alt={cat.label}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <span className="text-sm text-foreground-700 group-hover:text-foreground-950 transition-colors whitespace-nowrap">{cat.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.yoybuy_sheet')}
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-foreground-950 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.buy_now')}
            </a>
          </div>

          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-background-200/70 pb-4 pt-2 max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                {t('footer.home')}
              </Link>

              <Link
                to="/article/rukovodstvo-yoybuy-spreadsheet"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                Гид по покупкам
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-sm"></i>
                </span>
                {t('nav.yoybuy_sheet')}
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-sm"></i>
                </span>
                {t('nav.buy_now')}
              </a>

              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line text-sm"></i>
                  </span>
                  {t('nav.categories')}
                </span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {isMobileCatOpen && (
                <div className="pl-4 space-y-0.5 border-l-2 border-background-200/70 ml-5">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-7 h-7 rounded overflow-hidden bg-background-100 flex-shrink-0">
                        <img
                          src={categoryImages[cat.key]}
                          alt={cat.label}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <span className="whitespace-nowrap">{cat.label}</span>
                    </a>
                  ))}
                </div>
              )}

              <button
                onClick={() => setIsMobileArticlesOpen(!isMobileArticlesOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer rounded-lg"
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-article-line text-sm"></i>
                  </span>
                  Полезные статьи
                </span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isMobileArticlesOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {isMobileArticlesOpen && (
                <div className="pl-4 space-y-0.5 border-l-2 border-background-200/70 ml-5">
                  {allArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="block px-3 py-2 text-sm text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer rounded-lg line-clamp-1"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}